import UserProfileService from './../services/UserProfile.service'
import { getTeamLogo } from './getTeamLogo.function'

const WHITE_LABEL_RECRUIT_USERTYPE_ID = 133

/*
 * White-Label helpers (UAD-3667).
 *
 * White-label is a per-user flag (users.is_white_label) that admins toggle on a
 * leader. It is NOT a usertype/role — existing roles are unchanged. The flag
 * cascades down the signature chain, so the backend resolves an effective
 * `show_white_label` (true if the user OR any upline is flagged). When true,
 * USABG branding is hidden and the agency owner's Elite Agency Team logo
 * (team_logo) is shown instead.
 */
const isTruthy = val => [1, '1', true, 'true'].indexOf(val) > -1

/*
 * UAD-3724: hosts that serve the white-label experience. `acc.localhost` is the
 * local-dev alias (it resolves to 127.0.0.1 with no /etc/hosts entry).
 */
const WHITE_LABEL_HOSTS = [
  'agentcontractingcenter.com',
  'www.agentcontractingcenter.com',
  'beta.agentcontractingcenter.com',
  'acc.localhost',
]

/*
 * UAD-3724: the only routes the white-label host serves. Mirrors
 * WHITE_LABEL_ALLOWED_URLS (AgentNavLinks.component.jsx), widened to the
 * pre-auth screens, onboarding, account pages and enrollment links.
 */
const WHITE_LABEL_HOST_PATHS = [
  /^\/login\/?$/i,
  /^\/forgot-password\/?$/i,
  /^\/reset-password\/?$/i,
  /^\/welcome\/?$/i,
  /^\/agent-contracting\/?$/i,
  /^\/agency-branding\/?$/i,
  /^\/my-docs\/?$/i,
  /^\/account[\w-]*(\/[\w-]+)*\/?$/i,
  /^\/[A-Za-z0-9-]+\/enroll\/?$/i,
]

/*
 * @return {boolean}
 *   True when the page is being served from the white-label domain. Resolved
 *   from the host alone, so it works on the sign-in screens where no profile
 *   has been stored yet.
 */
const isWhiteLabelHost = () => WHITE_LABEL_HOSTS.indexOf(`${window.location.hostname}`.toLowerCase()) > -1

/*
 * @param {string} pathname
 * @return {boolean}
 *   True when the path is one the white-label domain is permitted to serve.
 */
const isWhiteLabelHostPath = pathname => WHITE_LABEL_HOST_PATHS.some(p => p.test(`${pathname}`))

/*
 * @return {boolean}
 *   True when the authenticated user should see the white-label UI (their own
 *   flag or an upline's, resolved server-side as `show_white_label`).
 *   Use on authenticated pages (dashboard, contracting).
 */
const isWhiteLabel = () => isTruthy(UserProfileService.get('show_white_label'))

/*
 * @param {object} upline
 *   An upline/recruiter record (e.g. the enrollment upline).
 * @return {boolean}
 *   True when enrolling under a white-label upline. Use on the enrollment page,
 *   where the relevant party is the upline rather than the new user.
 */
const isWhiteLabelUpline = upline => isTruthy(upline?.show_white_label) || isTruthy(upline?.is_white_label)

/*
 * @param {string|object} teamLogoOrTeam
 *   An Elite Agency Team logo path, or an object exposing `team_logo`.
 * @return {string|false}
 *   Resolved agency logo URL, or false when no logo is available.
 *   Wraps getTeamLogo in strict mode so callers can fall back to USABG
 *   branding when the agency has not set a logo.
 */
const getWhiteLabelLogo = teamLogoOrTeam => getTeamLogo(teamLogoOrTeam, true)

const isWhiteLabelAgency = () => isWhiteLabel() || isTruthy(UserProfileService.get('is_white_label'))

export {
  isWhiteLabel,
  isWhiteLabelAgency,
  isWhiteLabelUpline,
  getWhiteLabelLogo,
  isWhiteLabelHost,
  isWhiteLabelHostPath,
  WHITE_LABEL_RECRUIT_USERTYPE_ID,
}
